import { Check, ChevronRight, CircleDashed, Eye, KeyRound, Link2, Play, RadioTower, RefreshCw, Server, ShieldCheck, TestTube2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useSession } from "../auth/SessionContext";
import { Button, Card, DataAge, EmptyState, ErrorState, Field, InlineNotice, LoadingBlock, PageHeader } from "../components/ui";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest } from "../lib/api";
import { connectorLinkRequest } from "../lib/connector-link";
import type { ConnectorSummary, ConnectorTestResult, CursorPage, DiscoveredHostGroup, MappingPreview } from "../types";

type MappingForm = {
  buildingId: string;
  buildingName: string;
  client: string;
  region: string;
  networkItem: string;
  recorderItem: string;
};

const DEFAULT_MAPPING: MappingForm = {
  buildingId: "{$FARO_BUILDING_ID}", buildingName: "{$FARO_BUILDING_NAME}", client: "faro_client", region: "faro_region",
  networkItem: "^faro\\.network\\.up$", recorderItem: "^faro\\.recorder\\.up$",
};

export function ConnectorAdminPage() {
  useDocumentTitle("Conectores");
  const { csrfToken } = useSession();
  const connectors = useRemote(() => apiRequest<CursorPage<ConnectorSummary> | ConnectorSummary[]>("/api/v1/admin/connectors"), [], 60_000);
  const items = Array.isArray(connectors.data) ? connectors.data : connectors.data?.items ?? [];
  const zabbixConnector = items.find((connector) => connector.type === "zabbix");
  const zabbixState = connectors.loading && !connectors.data
    ? "Comprobando estado"
    : !zabbixConnector
      ? "Pendiente de vincular"
      : !zabbixConnector.enabled
        ? "Configurado, sin activar"
        : zabbixConnector.status === "failed"
          ? "Activo con error"
          : "Conector activo";
  const zabbixStateTone = zabbixConnector?.enabled
    ? zabbixConnector.status === "failed" ? "attention" : "active"
    : "pending";

  return (
    <div className="page">
      <PageHeader eyebrow="Administración" title="Zabbix y fuentes de monitoreo" description="Zabbix 7.0 LTS es la fuente prevista para supervisar la conectividad de los edificios y la disponibilidad de sus DVR/NVR." />
      <Card className="zabbix-overview" aria-labelledby="zabbix-overview-title">
        <span className="zabbix-overview__icon"><RadioTower size={26} aria-hidden="true" /></span>
        <div className="zabbix-overview__copy">
          <p className="eyebrow">Incluido en el despliegue de producción</p>
          <h2 id="zabbix-overview-title">Zabbix 7.0 LTS</h2>
          <p>El servicio ya forma parte de la plataforma. Para comenzar a recibir datos falta vincular su endpoint HTTPS, limitar los grupos autorizados y validar el inventario.</p>
          <ul className="zabbix-overview__facts" aria-label="Condiciones de la integración">
            <li><ShieldCheck size={14} aria-hidden="true" /> Solo lectura</li>
            <li><Check size={14} aria-hidden="true" /> Dos señales por edificio</li>
            <li><Play size={14} aria-hidden="true" /> Activación manual</li>
          </ul>
        </div>
        <span className={`zabbix-overview__state zabbix-overview__state--${zabbixStateTone}`}><span aria-hidden="true" />{zabbixState}</span>
      </Card>
      <InlineNotice tone="success" title="La conexión permanece bajo control del administrador">No se consulta ninguna dirección por sí sola. La primera solicitud ocurre al presionar “Probar conexión” y la sincronización comienza únicamente después de una vista previa válida y una activación confirmada.</InlineNotice>
      <section className="connector-layout">
        <Card>
          <div className="card-heading"><div><p className="eyebrow">Estado de integración</p><h2>Fuentes vinculadas</h2></div><RadioTower size={22} /></div>
          {connectors.loading && !connectors.data ? <LoadingBlock rows={3} /> : null}
          {connectors.error && !connectors.data ? <ErrorState error={connectors.error} onRetry={connectors.reload} /> : null}
          <div className="connector-list">
            {items.map((connector) => <article className="connector-item" key={connector.id}><span className={`connector-item__icon connector-item__icon--${connector.status}`}><Server size={19} /></span><div><strong>{connector.name}</strong><span>{connector.type === "mock" ? "Datos ficticios para la demostración local" : `Zabbix 7.0 · ${connector.urlDisplay || "dirección protegida"}`}</span><DataAge value={connector.lastSyncAt} prefix="Última sincronización" /></div><span className={`connector-state connector-state--${connector.enabled ? "enabled" : "disabled"}`}>{connector.enabled ? "Activo" : "Desactivado"}</span></article>)}
            {!connectors.loading && !items.length ? <EmptyState icon={Server} title="Zabbix todavía no está vinculado" description="El servicio está incluido en producción. Completá el asistente para autorizar el endpoint, los grupos y las señales que podrá leer." /> : null}
          </div>
        </Card>
        <ZabbixWizard csrfToken={csrfToken} existing={zabbixConnector ?? null} onActivated={connectors.reload} />
      </section>
    </div>
  );
}

function ZabbixWizard({ csrfToken, existing, onActivated }: { csrfToken: string | null; existing: ConnectorSummary | null; onActivated: () => void }) {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState("");
  const [token, setToken] = useState("");
  const [testResult, setTestResult] = useState<ConnectorTestResult | null>(null);
  const [groups, setGroups] = useState<DiscoveredHostGroup[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const mapping = DEFAULT_MAPPING;
  const [preview, setPreview] = useState<MappingPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [activated, setActivated] = useState(false);
  const [relinked, setRelinked] = useState(false);
  const relinking = Boolean(existing);
  const previewValid = Boolean(
    preview &&
    preview.matched > 0 &&
    preview.unmatched === 0 &&
    preview.ambiguous === 0 &&
    preview.readScopeValidated,
  );

  const call = async <T,>(path: string, body: unknown): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try { return await apiRequest<T>(path, { method: "POST", body, csrfToken }); }
    catch (nextError) { setError(nextError instanceof Error ? nextError.message : "No se pudo completar la operación."); return null; }
    finally { setBusy(false); }
  };

  const test = async (event: FormEvent) => {
    event.preventDefault();
    const result = await call<ConnectorTestResult>("/api/v1/admin/connectors/test", { type: "zabbix", url, token });
    if (result) { setTestResult(result); if (result.success) setStep(2); }
  };

  const discover = async () => {
    const result = await call<{ groups: DiscoveredHostGroup[] }>("/api/v1/admin/connectors/discover", { type: "zabbix", url, token });
    if (result) { setGroups(result.groups); setSelectedGroups(result.groups.map((group) => group.id)); }
  };

  const previewMapping = async () => {
    const result = await call<MappingPreview>("/api/v1/admin/connectors/preview", { type: "zabbix", url, token, hostGroupIds: selectedGroups, mapping });
    if (result) { setPreview(result); setConfirmed(false); setStep(4); }
  };

  const activate = async () => {
    if (!confirmed || !previewValid) return;
    const request = connectorLinkRequest(existing, { url, token, hostGroupIds: selectedGroups, mapping });
    const result = await call<ConnectorSummary>(request.path, request.body);
    if (!result) return;
    setRelinked(relinking);
    setActivated(true);
    setToken("");
    setStep(5);
    onActivated();
  };

  return (
    <Card className="wizard-card">
      <div className="card-heading"><div><p className="eyebrow">Fuente de producción</p><h2>Vincular Zabbix</h2></div><ShieldCheck size={22} /></div>
      <p className="wizard-intro">Este asistente conecta el inventario que ya exista en Zabbix. No crea equipos, no cambia problemas y no escribe en el servidor.</p>
      <ol className="wizard-steps" aria-label="Progreso de configuración">
        {["Acceso", "Grupos", "Contrato", "Revisión", "Activo"].map((label, index) => <li {...(step === index + 1 ? { "aria-current": "step" as const } : {})} className={step === index + 1 ? "active" : step > index + 1 ? "complete" : ""} key={label}><span>{step > index + 1 ? <Check size={14} /> : index + 1}</span><small>{label}</small></li>)}
      </ol>
      {error ? <InlineNotice tone="danger" title="No se pudo continuar">{error}</InlineNotice> : null}
      {step === 1 ? (
        <form className="wizard-body" onSubmit={test}>
          <InlineNotice title="Sin conexiones automáticas">Al presionar “Probar conexión” se consultará solamente el endpoint autorizado que escribas. Hasta entonces Zabbix permanece sin vincular.</InlineNotice>
          <Field label="Endpoint HTTPS de la API de Zabbix" hint="Debe terminar en api_jsonrpc.php y coincidir con un hostname autorizado."><span className="input-with-icon"><Link2 size={16} /><input type="url" required value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://zabbix.ejemplo/api_jsonrpc.php" /></span></Field>
          <Field label="Token restringido de solo lectura" hint="Usá un usuario de servicio no administrador. El token se cifra al guardar y no vuelve a mostrarse."><span className="input-with-icon"><KeyRound size={16} /><input type="password" required autoComplete="off" value={token} onChange={(event) => setToken(event.target.value)} placeholder="Pegá el token de servicio" /></span></Field>
          {testResult ? <InlineNotice tone={testResult.success ? "success" : "danger"} title={testResult.message}>{testResult.version ? `Zabbix ${testResult.version} · ${testResult.latencyMs ?? 0} ms` : null}</InlineNotice> : null}
          <div className="wizard-actions"><Button type="submit" loading={busy}><TestTube2 size={16} /> Probar conexión</Button></div>
        </form>
      ) : null}
      {step === 2 ? (
        <div className="wizard-body">
          <p>Elegí únicamente los grupos exclusivos de Centro Monitoreo Hikdos. Ningún host fuera de esta lista podrá importarse.</p>
          {!groups.length ? <EmptyState icon={CircleDashed} title="Inventario aún no consultado" description="Descubrí los grupos disponibles usando la conexión que acabás de validar." action={<Button loading={busy} onClick={() => void discover()}><RefreshCw size={16} /> Descubrir grupos</Button>} /> : (
            <div className="group-picker">{groups.map((group) => <label key={group.id}><input type="checkbox" checked={selectedGroups.includes(group.id)} onChange={(event) => setSelectedGroups((selected) => event.target.checked ? [...selected, group.id] : selected.filter((id) => id !== group.id))} /><span><strong>{group.name}</strong><small>{group.hosts} hosts disponibles</small></span></label>)}</div>
          )}
          <div className="wizard-actions"><Button variant="ghost" onClick={() => setStep(1)}>Volver</Button><Button disabled={!selectedGroups.length} onClick={() => setStep(3)}>Continuar <ChevronRight size={16} /></Button></div>
        </div>
      ) : null}
      {step === 3 ? (
        <div className="wizard-body">
          <p>Se validará un host por edificio y un contrato fijo que excluye cámaras, video y cualquier problema fuera del alcance acordado.</p>
          <InlineNotice title="Preflight completo y sin escritura">La vista previa también prueba historia, mantenimiento, problemas y eventos. No guarda inventario ni modifica Zabbix.</InlineNotice>
          <dl className="info-list">
            <div><dt>ID del edificio</dt><dd>{mapping.buildingId}</dd></div>
            <div><dt>Destino del sitio</dt><dd>{"{$FARO_SITE_HOST}"} · IP fija o DDNS</dd></div>
            <div><dt>Puerto externo del DVR/NVR</dt><dd>{"{$FARO_DVR_PORT}"} · TCP</dd></div>
            <div><dt>Cliente y región</dt><dd>{mapping.client} · {mapping.region}</dd></div>
            <div><dt>Señales</dt><dd>faro.network.up · faro.recorder.up</dd></div>
            <div><dt>Problemas permitidos</dt><dd>faro_scope=network · faro_scope=recorder</dd></div>
          </dl>
          <div className="wizard-actions"><Button variant="ghost" onClick={() => setStep(2)}>Volver</Button><Button loading={busy} onClick={() => void previewMapping()}><Eye size={16} /> Previsualizar</Button></div>
        </div>
      ) : null}
      {step === 4 && preview ? (
        <div className="wizard-body">
          <div className="preview-totals"><div><strong>{preview.matched}</strong><span>Coinciden</span></div><div><strong>{preview.unmatched}</strong><span>Sin mapear</span></div><div><strong>{preview.ambiguous}</strong><span>Ambiguos</span></div></div>
          <div className="preview-list">{preview.samples.slice(0, 8).map((sample) => <div key={sample.externalId}><span className={`preview-status preview-status--${sample.status}`} /> <strong>{sample.buildingName}</strong><span>{sample.client} · {sample.region}</span>{sample.reason ? <small>{sample.reason}</small> : null}</div>)}</div>
          {!previewValid ? <InlineNotice tone="danger" title="Activación bloqueada">Corregí todos los hosts sin mapear o ambiguos y repetí la vista previa. El grupo completo debe cumplir el contrato.</InlineNotice> : <InlineNotice tone="success" title="Mapeo y permisos validados">Los hosts coinciden y el token completó las {preview.permissions.length} lecturas permitidas.</InlineNotice>}
          {previewValid && relinking ? <InlineNotice title="Se reemplazará la conexión actual">Se guardarán esta dirección y este token en «{existing?.name}». El historial de edificios y problemas se conserva.</InlineNotice> : null}
          <label className="confirmation"><input type="checkbox" disabled={!previewValid} checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span><strong>Confirmo la activación manual</strong><small>Se importarán los problemas actuales en silencio y solo se avisarán eventos nuevos.</small></span></label>
          <div className="wizard-actions"><Button variant="ghost" onClick={() => setStep(3)}>Volver</Button><Button disabled={!confirmed || !previewValid} loading={busy} onClick={() => void activate()}><Play size={16} /> {relinking ? "Revincular conector" : "Activar conector"}</Button></div>
        </div>
      ) : null}
      {step === 5 && activated ? <div className="wizard-complete"><span><Check size={28} /></span><h3>{relinked ? "Zabbix quedó revinculado" : "Zabbix quedó vinculado"}</h3><p>La sincronización usará únicamente los grupos y señales validados. La primera importación toma los problemas existentes como contexto y evita una tormenta de avisos.</p><Button variant="secondary" onClick={() => { setStep(1); setActivated(false); setRelinked(false); setConfirmed(false); setPreview(null); }}><RadioTower size={16} /> Nueva configuración</Button></div> : null}
    </Card>
  );
}
